import Swal from 'sweetalert2';
import SweetToast from '../../SweetToast';
import _ from "lodash";
import axios from "axios";

function handleAxiosResponseError({ response }) {
  if (!response) {
    return;
  }
  const error = response?.data?.error;
  SweetToast.fire({
    icon: 'error',
    title: error ?? 'Something went wrong',
  });
}

function showAutoAssigningSwal() {
  Swal.fire({
    icon: 'info',
    showConfirmButton: false,
    title: `Auto Assigning <i class="fas fa-spinner fa-spin" />`,
    html: "We're using our data to automatically assign your chosen keywords to the best pages",
    allowOutsideClick: false,
    allowEscapeKey: false,
  }).then((result) => {
    addUsetifulTriggerElement();
  });
}

function showAllKeywordsAssignedWarning(onboardingStep) {
  Swal.fire({
    icon: 'warning',
    title: 'All Keywords Assigned',
    html: 'You can choose more keywords on the '+
      `${onboardingStep === 'COMPLETED' ? 'keyword ideas page.' : 'previous step.'}`,
  });
}

function showNoAssignableKeywordsWarning(url = null) {
  Swal.fire({
    icon: 'warning',
    title: 'No keywords to auto-assign',
    html: `None of your keywords are ranking in the top 50 Google results${url ? ' for ${url}' : ''}. `+
      'Please manually assign them to the most relevant pages on your website.',
  });
}

function autoAssignableKeywordsAvailable(keywords, url = null) {
  const assignableKeywords = keywords.filter((keyword) => {
    if (!keyword.ranking_url) {
      return false;
    }

    const ranksBelowThreshold = keyword.ranking_position <= 50;

    if (!url) {
      return ranksBelowThreshold;
    }

    return ranksBelowThreshold && parsedUrlsAreEqual(keyword.ranking_url, url);
  });

  return assignableKeywords?.length > 0 ?? false;
}

function parsedUrlsAreEqual(urlA, urlB) {
  urlA = urlA.endsWith('/') ? urlA : urlA + '/';
  urlB = urlB.endsWith('/') ? urlB : urlB + '/';
  return urlA === urlB;
}

function addUsetifulTriggerElement() {
  if (!document.getElementById('trigger-usetiful')) {
    const onboardingEl = document.querySelector('.sitemap_view');
    const newDiv = document.createElement('div');
    newDiv.id = 'trigger-usetiful';
    onboardingEl.appendChild(newDiv);
  }
}

const SET_PROCESSING = 'SET_PROCESSING';
const SET_STRATEGY = 'SET_STRATEGY';
const SET_SHOW_ADD_KEYWORD_MODAL_STATE = 'SET_SHOW_ADD_KEYWORD_MODAL_STATE';
const SET_SHOW_ADD_PAGE_MODAL_STATE = 'SET_SHOW_ADD_PAGE_MODAL_STATE';
const SET_SHOW_KEYWORD_REVIEW_MODAL = 'SET_SHOW_KEYWORD_REVIEW_MODAL';
const SET_SELECTED_PAGE = 'SET_SELECTED_PAGE';
const SET_SELECTED_PAGE_INDEX = 'SET_SELECTED_PAGE_INDEX';
const REORDER_SITEMAP_PAGES = 'REORDER_SITEMAP_PAGES';
const SET_PAGE_REFRESHING_STATE = 'SET_PAGE_REFRESHING_STATE';
const UPDATE_SITEMAP_PAGE = 'UPDATE_SITEMAP_PAGE';
const SET_PAGE_KEYWORDS_REVIEWS_POLL_COUNT = 'SET_PAGE_KEYWORDS_REVIEWS_POLL_COUNT'
const SET_IS_ONBOARDING = 'SET_IS_ONBOARDING';
const IS_EDITING = 'IS_EDITING';
const SET_SHOW_KIT_MODAL = 'SET_SHOW_KIT_MODAL';
const SET_CAN_SEE_KIT = 'SET_CAN_SEE_KIT';

const state = {
  strategy: {},
  processing: false,
  showAddKeywordModal: false,
  showAddPageModal: false,
  showKeywordReviewModal: false,
  pageKeywordsReviewsPollingCount: 0,
  selectedPage: null,
  selectedPageIndex: null,
  isOnboarding: false,
  isEditing: false,
  canSeeKit: false,
  showKitModal: false,
};

const getters = {
  processing: ({ processing }) => processing,
  strategy: ({ strategy }) => strategy,
  sitemap: ({ strategy }) => strategy.sitemap,
  pages: ({ strategy }) => strategy.pages,
  showAddKeywordModal: ({ showAddKeywordModal }) => showAddKeywordModal,
  showAddPageModal: ({ showAddPageModal }) => showAddPageModal,
  showKeywordReviewModal: ({ showKeywordReviewModal }) => showKeywordReviewModal,
  selectedPage: ({ selectedPage }) => selectedPage,
  selectedPageIndex: ({ selectedPageIndex }) => selectedPageIndex,
  unassignedKeywords: ({ strategy }) => strategy?.unassigned_keywords ?? [],
  onboardingStep: ({ strategy }) => strategy?.onboarding_step,
  strategyId: ({ strategy }) => strategy?.id,
  websiteDomain: ({ strategy }) => strategy?.website?.website_url.replace(/\/+$/, ""),
  pendingReviews: (state, { sitemap }) => sitemap?.filter(page => page.review_status === 'pending'),
  hasPendingReviews: (state, { pendingReviews }) => pendingReviews?.length > 0,
  reviewAvailable: (state, { sitemap }) => sitemap?.filter(page => page.review_status === 'unreviewed' && page.keywords?.length > 0)?.length > 0,
  reviewRequired: (state, { sitemap }) => sitemap?.filter(page => page.keywords?.length > 0).some(page => page.review_status === 'unreviewed'),
  isOnboarding: ({ isOnboarding }) => isOnboarding,
  isEditing: ({ isEditing }) => isEditing,
  showKitModal: ({ showKitModal }) => showKitModal,
};

const actions = {
  fetchStrategy({commit, dispatch, getters}) {
    commit(SET_PROCESSING, true);

    let url = '/strategy/fetch-strategy';
    if (getters.isOnboarding) {
      const query = new URLSearchParams();
      query.append('isOnboarding',  getters.isOnboarding);
      url = url+'?' + query.toString();
    }

    return axios.get(url)
      .then(async ({data}) => {
        commit(SET_STRATEGY, data.strategy);
        if (data.strategy.sitemap.length === 0) {
          await dispatch('addHomepageToSitemap');
          dispatch('emptySitemapAutoAssign');
          return;
        }
        addUsetifulTriggerElement();
      })
      .catch(handleAxiosResponseError)
      .finally(() => {
        commit(SET_PROCESSING, false);
      });
  },
  basicFetchStrategy({commit}) {
    return axios.get('/strategy/fetch-strategy')
      .then(async ({data}) => {
        commit(SET_STRATEGY, data.strategy);
      })
      .catch(handleAxiosResponseError);
  },
  emptySitemapAutoAssign({dispatch, getters}) {
    const { unassignedKeywords } = getters
    if (unassignedKeywords.length === 0 || !autoAssignableKeywordsAvailable(unassignedKeywords)) {
      addUsetifulTriggerElement();
      return;
    }

    dispatch('autoAssignPagesAndKeywords');
  },
  tryAutoAssignPagesAndKeywords({dispatch, getters}) {
    const { strategy, unassignedKeywords } = getters
    if (unassignedKeywords.length === 0) {
      showAllKeywordsAssignedWarning(strategy.onboarding_step);
      return;
    }
    if (!autoAssignableKeywordsAvailable(unassignedKeywords)) {
      showNoAssignableKeywordsWarning();
      return;
    }

    dispatch('autoAssignPagesAndKeywords');
  },
  autoAssignPagesAndKeywords({commit}) {
    showAutoAssigningSwal();
    commit(SET_PROCESSING, true);
    axios.get('/strategy/auto-assign-sitemap-pages')
      .then(({ data }) => {
        commit(SET_STRATEGY, data.strategy);
        const keywordText = `${data.keywordsCount} keyword${data.keywordsCount > 1 ? 's' : ''}`;
        const pageText = `${data.pagesCount} page${data.pagesCount > 1 ? 's' : ''}`;
        Swal.update({
          icon: 'success',
          title: 'Auto-assign complete',
          html: `We assigned ${keywordText} to ${pageText}. <br>`+
            'Look out for the star symbol next to your assigned keywords to identify these.',
          showConfirmButton: true,
          allowOutsideClick: true,
          allowEscapeKey: true,
        });
      })
      .catch(handleAxiosResponseError)
      .finally(() => {
        commit(SET_PROCESSING, false);
      });
  },
  autoAssignKeywordsToPage({commit, getters}, pageIndex) {
    const {strategy, unassignedKeywords, sitemap} = getters;
    if (unassignedKeywords.length === 0) {
      showAllKeywordsAssignedWarning(strategy.onboarding_step);
      return;
    }
    if (!autoAssignableKeywordsAvailable(unassignedKeywords, sitemap[pageIndex].url)) {
      showNoAssignableKeywordsWarning();
      return;
    }
    showAutoAssigningSwal();
    commit(SET_PROCESSING, true);
    return axios.post('/strategy/auto-assign-keywords-to-page', {
        pageIndex,
      })
      .then(({ data }) => {
        commit(SET_STRATEGY, data.strategy);
        const keywordText = `${data.keywordsCount} keyword${data.keywordsCount > 1 ? 's' : ''}`;
        Swal.fire({
          icon: 'success',
          title: 'Auto Assign completed',
          html: `We assigned ${keywordText} to this page.`,
        });
      })
      .catch(handleAxiosResponseError)
      .finally(() => {
        commit(SET_PROCESSING, false);
      });
  },
  saveSitemap({commit, dispatch}, {strategyId}) {
    commit(SET_PROCESSING, true);
    Swal.fire({
      icon: 'info',
      title: 'Saving keyword sitemap',
      html: "Would you also like to refresh your SEO actions?",
      confirmButtonText: 'Save & Refresh Actions',
      confirmButtonColor: '#37B2A8',
      showDenyButton: true,
      denyButtonText: 'Save Only',
      denyButtonColor: '#8362F9',
      reverseButtons: true,
      preConfirm: () => {
        return dispatch('saveSitemapAxios', {strategyId, recrawl: true});
      },
      preDeny: () => {
        return dispatch('saveSitemapAxios', {strategyId});
      }
    });
  },
  saveSitemapAxios({commit}, {strategyId, recrawl}) {
    const title = recrawl
      ? 'Saving the sitemap and refreshing your SEO actions!'
      : 'Saving the sitemap only';

    Swal.fire({
      icon: 'info',
      title,
      allowOutsideClick: false,
      backdrop: true,
    });
    Swal.showLoading();

    axios.post('strategy/save-sitemap',{
      strategyId,
      recrawl,
    })
      .then(({ data }) => {
        commit(SET_STRATEGY, data.strategy);
        Swal.fire({
          title: 'Saved successfully',
          icon: 'success',
          html: data.successMessage,
        });
      })
      .catch(({ response }) => {
        Swal.fire({
          icon: 'error',
          title: 'Something went wrong',
          html: response.data.error,
        })
      })
      .finally(() => {
        commit(SET_PROCESSING, false);
      });
  },
  addKeywordsToPage({commit, getters}, keywords) {
    commit(SET_PROCESSING, true);
    const { selectedPage } = getters;
    return axios.post('strategy/add-keywords',{
        pageUrl: selectedPage.url,
        keywords: keywords,
        isOnboarding: getters.isOnboarding,
      })
      .then(({ data }) => {
        commit(SET_STRATEGY, data.strategy);
      })
      .catch(handleAxiosResponseError)
      .finally(() => {
        commit(SET_PROCESSING, false);
      });
  },
  removeKeywordFromPage({commit, getters}, payload) {
    if (state.canSeeKit)  {
      commit(SET_SHOW_KIT_MODAL, true);
      return;
    }

    const { page, assignedKeyword } = payload;
    const { url: pageUrl } = page;
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      html: `Please confirm you want to remove "${assignedKeyword.keyword}" from ${pageUrl}`,
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonText: 'Remove',
      confirmButtonColor: '#d33',
      showLoaderOnConfirm: true,
      backdrop: true,
      preConfirm: async () => {
        await axios.post('/strategy/remove-keyword', {
            pageUrl,
            assignedKeyword,
            isOnboarding: getters.isOnboarding,
          })
          .then(response => {
            commit(SET_STRATEGY, response.data.strategy);
            return response;
          }).catch(({response}) => {
            const { error } = response.data;
            Swal.showValidationMessage(
              `Request failed: ${error}`
            );
          });
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Removed successfully',
          icon: 'success'
        });
      }
    });
  },
  reorderSitemapPage({commit, dispatch}, payload) {
    const {pageIndex, direction} = payload;
    const newIndex =
      direction === 'up' ? pageIndex - 1 : pageIndex + 1;
    commit(REORDER_SITEMAP_PAGES, {pageIndex, newIndex});
    dispatch('debounceSaveSitemapOrder');
  },
  addPagesToSitemap({commit}, pages) {
    return axios.post('/strategy/add-pages-to-sitemap', {
        pages,
      })
      .then(({ data }) => {
        commit(SET_STRATEGY, data.strategy);
      })
      .catch(handleAxiosResponseError);
  },
  createPageAndAddToSitemap({commit}, {pageUrl, newPage}) {
    return new Promise((resolve, reject) => {
      commit(SET_PROCESSING, true);
      axios.post('/strategy/add-custom-page-to-sitemap', {pageUrl, newPage})
        .then(({ data }) => {
          commit(SET_STRATEGY, data.strategy);
          resolve();
        })
        .catch((error) => {
          const { response } = error;
          if (response.status === 422) {
            reject(response.data.errors);
            return;
          }
          handleAxiosResponseError(error);
        }).finally(() => {
          commit(SET_PROCESSING, false);
        });
    });
  },
  removePageFromSitemap({commit, getters}, page) {
    if (state.canSeeKit)  {
      commit(SET_SHOW_KIT_MODAL, true);
      return;
    }

    const { url: pageUrl } = page;
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      html: `Please confirm you want to remove ${pageUrl} from your keyword sitemap`,
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonText: 'Remove',
      confirmButtonColor: '#d33',
      showLoaderOnConfirm: true,
      backdrop: true,
      preConfirm: async () => {
        await axios.post('/strategy/remove-page-from-sitemap', {
            pageUrl,
            isOnboarding: getters.isOnboarding,
          }).then(({ data }) => {
            commit(SET_STRATEGY, data.strategy);
          }).catch(({response}) => {
          const { error } = response.data;
            Swal.showValidationMessage(
              `Request failed: ${error}`
            );
          });
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Removed successfully',
          icon: 'success'
        });
      }
    });
  },
  debounceSaveSitemapOrder: _.debounce( ({dispatch}) => {
    dispatch('saveSitemapOrder');
  }, 2000),
  saveSitemapOrder({getters}) {
    let { sitemap } = getters;
    return axios.post('/strategy/save-sitemap-order', {
      sitemap,
    }).catch(handleAxiosResponseError);
  },
  refreshSitemapPage({commit}, page) {
    commit(SET_PAGE_REFRESHING_STATE, {page, refreshState: true});
    const {url: pageUrl} = page;
    axios.post('/strategy/refresh-sitemap-page', {
      pageUrl,
    }).then(({data}) => {
      const {pageData} = data;
      commit(UPDATE_SITEMAP_PAGE, {page, pageData});
      commit(SET_PAGE_REFRESHING_STATE, {page, refreshState: false});
    }).catch(({response}) => {
      const {pageData} = response.data;
      commit(UPDATE_SITEMAP_PAGE, {page, pageData});
      commit(SET_PAGE_REFRESHING_STATE, {page, refreshState: false});
    });
  },
  addHomepageToSitemap({commit, dispatch}) {
    return axios.post('/strategy/add-homepage-to-sitemap')
      .then(({ data }) => {
        commit(SET_STRATEGY, data.strategy);
        if (data.refresh) {
          dispatch('refreshSitemapPage', data.strategy.sitemap[0]);
        }
      })
      .catch(handleAxiosResponseError)
      .finally(() => {
        commit(SET_PROCESSING, false);
      });
  },
  openAddKeywordModal({commit, state}, {page}) {
    if (state.canSeeKit)  {
      commit(SET_SHOW_KIT_MODAL, true);
      return;
    }
    commit(SET_SELECTED_PAGE, page);
    commit(SET_SHOW_ADD_KEYWORD_MODAL_STATE, true);
  },
  closeAddKeywordModal({commit}) {
    commit(SET_SHOW_ADD_KEYWORD_MODAL_STATE, false);
    commit(SET_SELECTED_PAGE, null);
  },
  openKeywordReviewModal({commit}) {
    commit(SET_SHOW_KEYWORD_REVIEW_MODAL, true);
  },
  closeKeywordReviewModal({commit}) {
    commit(SET_SHOW_KEYWORD_REVIEW_MODAL, false);
  },
  async reviewSitemapKeywords({dispatch}) {
    try {
      await axios.post('/strategy/review-sitemap-keywords')
      await dispatch('closeKeywordReviewModal');
      dispatch('pollPageKeywordsReviews');
    } catch (e) {
      handleAxiosResponseError(e)
    }
  },
  async pollPageKeywordsReviews({commit, dispatch, getters, state}) {
    if (
      (
        state.pageKeywordsReviewsPollingCount >= 100
        || ! getters.hasPendingReviews
      )
      && state.pageKeywordsReviewsPollingCount > 0
    ) {
      commit(SET_PAGE_KEYWORDS_REVIEWS_POLL_COUNT, 0);
      return;
    }

    commit(SET_PAGE_KEYWORDS_REVIEWS_POLL_COUNT, state.pageKeywordsReviewsPollingCount + 1);

    await dispatch('basicFetchStrategy');

    await new Promise(resolve => setTimeout(resolve, 8000));

    return dispatch('pollPageKeywordsReviews');
  },
  openAddPageModal: ({commit}) => {
    if (state.canSeeKit)  {
      commit(SET_SHOW_KIT_MODAL, true);
      return;
    }
    commit(SET_SHOW_ADD_PAGE_MODAL_STATE, true)
  },
  closeAddPageModal: ({commit}) => commit(SET_SHOW_ADD_PAGE_MODAL_STATE, false),
  updateSitemapPageUrl: ({commit}, page) => {
    if (state.canSeeKit)  {
      commit(SET_SHOW_KIT_MODAL, true);
      return;
    }

    const {url: pageUrl} = page;
    Swal.fire({
      title: 'Update your URL',
      input: 'text',
      inputValue: pageUrl,
      showCancelButton: true,
      backdrop: true,
      showLoaderOnConfirm: true,
      inputValidator(inputValue) {
        if (pageUrl === inputValue) {
          return "Please edit the url";
        }
      },
      preConfirm: (updatedUrl) => {
        return axios.post('/strategy/update-sitemap-page-url', {
          updatedUrl,
          pageUrl
        }).then(({ data }) => {
          return data;
        })
        .catch(({ response }) => {
          const { data } = response;
          if (response.status === 422) {
            const { errors } = data;
            const { updatedUrl } = errors;
            Swal.showValidationMessage(updatedUrl[0]);
          } else {
            const { error } = data;
            Swal.showValidationMessage(error);
          }
          return false;
        });
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then(({isConfirmed, value}) => {
      if (isConfirmed) {
        const { strategy } = value;
        commit(SET_STRATEGY, strategy);
        Swal.close();
        SweetToast.fire({icon:'success', title: 'Url updated successfully'});
      }
    });
  },
  toggleEditing({commit}) {
    commit(IS_EDITING);
  },
  setIsOnboarding({commit}, isOnboarding) {
    commit(SET_IS_ONBOARDING, isOnboarding);
  },
  setCanSeeKit({commit}, canSeeKit) {
    commit(SET_CAN_SEE_KIT, canSeeKit);
  },
  dismissKitModal({commit}) {
    commit(SET_SHOW_KIT_MODAL, false);
    commit(SET_CAN_SEE_KIT, false);
  },
};

const mutations = {
  [SET_PROCESSING](state, processingState){
    state.processing = processingState;
  },
  [SET_STRATEGY](state, strategy) {
    state.strategy = {...strategy};
  },
  [SET_SHOW_ADD_KEYWORD_MODAL_STATE](state, modalState) {
    state.showAddKeywordModal = modalState;
  },
  [SET_SHOW_KEYWORD_REVIEW_MODAL](state, modalState) {
    state.showKeywordReviewModal = modalState;
  },
  [SET_SHOW_ADD_PAGE_MODAL_STATE](state, modalState) {
    state.showAddPageModal = modalState;
  },
  [SET_SELECTED_PAGE](state, page) {
    state.selectedPage = page;
  },
  [SET_SELECTED_PAGE_INDEX](state, pageIndex) {
    state.selectedPageIndex = pageIndex;
  },
  [IS_EDITING](state) {
    state.isEditing = !state.isEditing;
  },
  [REORDER_SITEMAP_PAGES](state, {pageIndex, newIndex}) {
    let sitemap = state.strategy.sitemap;
    [sitemap[pageIndex], sitemap[newIndex]] = [sitemap[newIndex], sitemap[pageIndex]];
  },
  [SET_PAGE_REFRESHING_STATE](state, {page, refreshState}) {
    const {strategy} = state;
    const {sitemap} = strategy;
    const pageIndex = sitemap.findIndex((sitemapPage) => sitemapPage.url === page.url);
    sitemap[pageIndex].refreshing = refreshState;
    state.strategy.sitemap = sitemap;
  },
  [UPDATE_SITEMAP_PAGE](state, {page, pageData}) {
    const {strategy} = state;
    const {sitemap} = strategy;
    const pageIndex = sitemap.findIndex((sitemapPage) => sitemapPage.url === page.url);
    sitemap[pageIndex].pageTitle = pageData.pageTitle;
    sitemap[pageIndex].refreshError = pageData.refreshError;
    state.strategy.sitemap = sitemap;
  },
  [SET_PAGE_KEYWORDS_REVIEWS_POLL_COUNT](state, count) {
    state.pageKeywordsReviewsPollingCount = count;
  },
  [SET_IS_ONBOARDING](state, isOnboarding) {
    state.isOnboarding = isOnboarding;
  },
  [SET_SHOW_KIT_MODAL](state, showKitModal = true) {
    state.showKitModal = showKitModal;
  },
  [SET_CAN_SEE_KIT](state, canSeeKit) {
    state.canSeeKit = canSeeKit;
  },
};

const onboarding = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
export default onboarding;
